import { faArrowLeft, faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DEFAULT_FILTER } from 'features/job-offers/list/components/JobOffersListComponent.constants';
import { useI18n } from 'hooks/useI18n';
import { useRouter } from 'next/dist/client/router';
import Link from 'next/link';
import { useState } from 'react';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { getUrlParamsFromJobOffersFilter } from 'utils/job-offers-filter';
import { getLocale } from 'utils/localize.utils';
import TopbarCompaniesSubnav from './TopbarCompaniesSubnav';
import TopbarFreelanceSubnav from './TopbarFreelanceSubnav';
import {
  StyledSubnav,
  StyledSubnavClose,
  StyledSubnavTitle,
  StyledTopbarNav,
  StyledTopbarNavItem,
  StyledTopbarNavItemIcon
} from './TopbarNav.styled';
import TopbarTalentSubnav from './TopbarTalentSubnav';
import NewTopbarCompaniesSubnav from './NewSubnav/NewTopbarCompaniesSubnav';
import NewTopbarFreelanceSubnav from './NewSubnav/NewTopbarFreelanceSubnav';
import NewTopbarTalentSubnav from './NewSubnav/NewTopbarTalentSubnav';

const TopbarNav: React.FC = () => {
  const { t } = useI18n('topbar');
  const { pathname, locale } = useRouter();

  const getNavItemClassname = (path: string): string => {
    return pathname.startsWith(path) ? 'active' : '';
  };

  const isTalent = pathname.startsWith('/talent');
  const isCompanies =
    pathname.startsWith('/empresas') || pathname.startsWith('/companies');
  const isFreelance = pathname.startsWith('/freelance');

  const [talentSubNavActive, setTalentSubnavActive] = useState(false);
  const [companiesSubNavActive, setTCompaniesSubnavActive] = useState(false);
  const [freelanceSubNavActive, setFreelanceSubnavActive] = useState(false);

  return (
    <StyledTopbarNav
      aria-label={t('nav.label')}
      isTalent={isTalent}
      isCompanies={isCompanies}
      isFreelance={isFreelance}
    >
      <ul>
        <li>
          <Link
            href={`${getAppUrl(
              APP_URLS.jobOffers,
              locale
            )}?${getUrlParamsFromJobOffersFilter(
              DEFAULT_FILTER,
              getLocale(locale)
            )}`}
            passHref
          >
            <StyledTopbarNavItem className={getNavItemClassname('/job-offers')}>
              {t('nav.offers')}
            </StyledTopbarNavItem>
          </Link>
        </li>

        <li className={'desktopSubnavTrigger'}>
          <Link href={`${getAppUrl(APP_URLS.companies, locale)}`} passHref>
            <StyledTopbarNavItem className={getNavItemClassname('/companies')}>
              {t('nav.companies')}
            </StyledTopbarNavItem>
          </Link>
          <StyledSubnav>
            <NewTopbarCompaniesSubnav />
          </StyledSubnav>
        </li>

        <li className="desktopSubnavTrigger">
          <Link href={`${getAppUrl(APP_URLS.talent, locale)}`} passHref>
            <StyledTopbarNavItem className={getNavItemClassname('/talent')}>
              {t('nav.talent')}
            </StyledTopbarNavItem>
          </Link>
          <StyledSubnav>
            <NewTopbarTalentSubnav />
          </StyledSubnav>
        </li>

        <li className={'desktopSubnavTrigger'}>
          <Link
            href={`${getAppUrl(APP_URLS.freelanceCompanies, locale)}`}
            passHref
          >
            <StyledTopbarNavItem className={getNavItemClassname('/freelance')}>
              {t('nav.freelance')}
            </StyledTopbarNavItem>
          </Link>
          <StyledSubnav>
            <NewTopbarFreelanceSubnav />
          </StyledSubnav>
        </li>

        <li className="mobileSubnavTrigger">
          <StyledTopbarNavItem
            as="button"
            className={getNavItemClassname('/talent')}
            onClick={() => setTCompaniesSubnavActive(true)}
          >
            {t('nav.companies')}
            <StyledTopbarNavItemIcon>
              <FontAwesomeIcon icon={faArrowRight} />
            </StyledTopbarNavItemIcon>
          </StyledTopbarNavItem>
          <StyledSubnav className={companiesSubNavActive ? 'is-active' : ''}>
            <StyledSubnavClose onClick={() => setTCompaniesSubnavActive(false)}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {t('talent_subnav.back')}
            </StyledSubnavClose>
            <StyledSubnavTitle>{t('nav.companies')}</StyledSubnavTitle>
            <TopbarCompaniesSubnav />
          </StyledSubnav>
        </li>

        <li className="mobileSubnavTrigger">
          <StyledTopbarNavItem
            as="button"
            className={getNavItemClassname('/talent')}
            onClick={() => setTalentSubnavActive(true)}
          >
            {t('nav.talent')}
            <StyledTopbarNavItemIcon>
              <FontAwesomeIcon icon={faArrowRight} />
            </StyledTopbarNavItemIcon>
          </StyledTopbarNavItem>
          <StyledSubnav className={talentSubNavActive ? 'is-active' : ''}>
            <StyledSubnavClose onClick={() => setTalentSubnavActive(false)}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {t('talent_subnav.back')}
            </StyledSubnavClose>
            <StyledSubnavTitle>{t('nav.talent')}</StyledSubnavTitle>
            <TopbarTalentSubnav />
          </StyledSubnav>
        </li>

        <li className="mobileSubnavTrigger">
          <StyledTopbarNavItem
            as="button"
            className={getNavItemClassname('/freelance')}
            onClick={() => setFreelanceSubnavActive(true)}
          >
            {t('nav.freelance')}
            <StyledTopbarNavItemIcon>
              <FontAwesomeIcon icon={faArrowRight} />
            </StyledTopbarNavItemIcon>
          </StyledTopbarNavItem>
          <StyledSubnav className={freelanceSubNavActive ? 'is-active' : ''}>
            <StyledSubnavClose onClick={() => setFreelanceSubnavActive(false)}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {t('talent_subnav.back')}
            </StyledSubnavClose>
            <StyledSubnavTitle>{t('nav.freelance')}</StyledSubnavTitle>
            <TopbarFreelanceSubnav />
          </StyledSubnav>
        </li>

        <li>
          <Link href={`${getAppUrl(APP_URLS.team, locale)}`} passHref>
            <StyledTopbarNavItem className={getNavItemClassname('/team')}>
              {t('nav.us')}
            </StyledTopbarNavItem>
          </Link>
        </li>

        <li>
          <Link href={`${getAppUrl(APP_URLS.blog, locale)}`} passHref>
            <StyledTopbarNavItem className={getNavItemClassname('/blog')}>
              {t('nav.blog')}
            </StyledTopbarNavItem>
          </Link>
        </li>

        <li>
          <Link href={`${getAppUrl(APP_URLS.contact, locale)}`} passHref>
            <StyledTopbarNavItem className={getNavItemClassname('/contact')}>
              {t('nav.contact')}
            </StyledTopbarNavItem>
          </Link>
        </li>
      </ul>
    </StyledTopbarNav>
  );
};

export default TopbarNav;
